import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { Content, ContentHeader, Header, Page, SupportButton } from '@backstage/core-components';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  CatalogFilterLayout,
  EntityKindPicker,
  EntityListProvider,
  EntityOwnerPicker,
  EntitySearchBar,
  useEntityList,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import { usePermission } from '@backstage/plugin-permission-react';
import { TemplateListPageProps } from '@backstage/plugin-scaffolder/alpha';
import { TemplateGroupFilter } from '@backstage/plugin-scaffolder-react';
import {
  ScaffolderPageContextMenu,
  TemplateCategoryPicker,
  TemplateGroups,
} from '@backstage/plugin-scaffolder-react/alpha';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { Theme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AddCircleOutline from '@material-ui/icons/AddCircleOutline';
import { useCallback, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { DEFAULT_FILTER_TAGS } from './defaultFilterTags';
import { EntityTagOrPicker } from './EntityTagOrPicker';

type RegisterExistingButtonProps = {
  title: string;
  to?: string;
};

const RegisterExistingButton = ({ title, to }: RegisterExistingButtonProps) => {
  const { allowed } = usePermission({ permission: catalogEntityCreatePermission });
  const isExtraSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));

  if (!to || !allowed) {
    return null;
  }

  if (isExtraSmallScreen) {
    return (
      <IconButton component={RouterLink} color="primary" title={title} aria-label={title} size="small" to={to}>
        <AddCircleOutline />
      </IconButton>
    );
  }

  return (
    <Button component={RouterLink} variant="outlined" color="primary" to={to}>
      {title}
    </Button>
  );
};

const getEntityIdentifier = (entity: TemplateEntityV1beta3): string => stringifyEntityRef(entity);

const SCAFFOLDER_ROUTES = {
  registerExistingComponent: '/catalog-import',
  edit: '/create/edit',
  actions: '/create/actions',
  tasks: '/create/tasks',
  templatingExtensions: '/create/templating-extensions',
  templateDetails: (namespace: string, templateName: string) => `/create/templates/${namespace}/${templateName}`,
} as const;

/**
 * Builds the groups array for TemplateGroups from caller-supplied group definitions.
 *
 * @remarks
 * Pure function — computes the set of matched entity IDs in a single pass over
 * `allEntities` before constructing the "Other" group predicate, so the "Other"
 * filter does not rely on evaluation order or side effects. This avoids the
 * correctness hazard of mutating a closed-over Set as a side effect of the
 * named groups' filter calls.
 *
 * @internal Exported for unit testing only.
 */
export const createTemplateGroups = (
  givenGroups: TemplateGroupFilter[],
  allEntities: TemplateEntityV1beta3[],
): TemplateGroupFilter[] => {
  if (!givenGroups.length) {
    return [{ title: 'All Templates', filter: () => true }];
  }

  const matchedIds = new Set(
    allEntities.filter(entity => givenGroups.some(group => group.filter(entity))).map(getEntityIdentifier),
  );

  return [
    ...givenGroups,
    {
      title: 'Other',
      filter: (entity: TemplateEntityV1beta3) => !matchedIds.has(getEntityIdentifier(entity)),
    },
  ];
};

/**
 * Inner content component. Must be rendered inside EntityListProvider so that
 * useEntityList() can access the current filtered entity list for pure group
 * computation.
 */
const TemplateListContent = (props: TemplateListPageProps) => {
  const { TemplateCardComponent, groups: givenGroups = [], templateFilter, contextMenu, headerOptions } = props;

  const navigate = useNavigate();
  const { entities } = useEntityList();
  const allEntities = entities as TemplateEntityV1beta3[];
  const groups = useMemo(() => createTemplateGroups(givenGroups, allEntities), [givenGroups, allEntities]);

  const onTemplateSelected = useCallback(
    (template: TemplateEntityV1beta3) => {
      const { namespace, name } = parseEntityRef(stringifyEntityRef(template));

      navigate(SCAFFOLDER_ROUTES.templateDetails(namespace, name));
    },
    [navigate],
  );

  const scaffolderPageContextMenuProps = useMemo(
    () => ({
      onEditorClicked: contextMenu?.editor !== false ? () => navigate(SCAFFOLDER_ROUTES.edit) : undefined,
      onActionsClicked: contextMenu?.actions !== false ? () => navigate(SCAFFOLDER_ROUTES.actions) : undefined,
      onTasksClicked: contextMenu?.tasks !== false ? () => navigate(SCAFFOLDER_ROUTES.tasks) : undefined,
      onTemplatingExtensionsClicked:
        contextMenu?.templatingExtensions !== false
          ? () => navigate(SCAFFOLDER_ROUTES.templatingExtensions)
          : undefined,
    }),
    [contextMenu, navigate],
  );

  return (
    <Page themeId="home">
      <Header
        pageTitleOverride="Create a New Component"
        title="Create a New Component"
        subtitle="Create new software components using standard templates"
        {...headerOptions}
      >
        <ScaffolderPageContextMenu {...scaffolderPageContextMenuProps} />
      </Header>
      <Content>
        <ContentHeader>
          <RegisterExistingButton
            title="Register Existing Component"
            to={SCAFFOLDER_ROUTES.registerExistingComponent}
          />
          <SupportButton>Create new software components using standard templates in your organization</SupportButton>
        </ContentHeader>
        <CatalogFilterLayout>
          <CatalogFilterLayout.Filters>
            <EntitySearchBar />
            <EntityKindPicker initialFilter="template" hidden />
            <UserListPicker initialFilter="all" availableFilters={['all', 'starred']} />
            <TemplateCategoryPicker />
            <EntityTagOrPicker initialFilter={[...DEFAULT_FILTER_TAGS]} />
            <EntityOwnerPicker />
          </CatalogFilterLayout.Filters>
          <CatalogFilterLayout.Content>
            <TemplateGroups
              groups={groups}
              templateFilter={templateFilter}
              TemplateCardComponent={TemplateCardComponent}
              onTemplateSelected={onTemplateSelected}
            />
          </CatalogFilterLayout.Content>
        </CatalogFilterLayout>
      </Content>
    </Page>
  );
};

/**
 * Custom TemplateListPage that uses OR-based tag filtering.
 *
 * @remarks
 * Replaces the stock TemplateListPage so that selecting multiple tags from
 * the sidebar shows templates matching ANY tag (OR), not ALL tags (AND).
 * The default selected tags are driven by DEFAULT_FILTER_TAGS.
 */
export const CustomTemplateListPage = (props: TemplateListPageProps) => (
  <EntityListProvider>
    <TemplateListContent {...props} />
  </EntityListProvider>
);
